/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AliasFormModal.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Modal component for creating/editing domain aliases.
────────────────────────────────────────────────────────────────*/

import { X } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import Toggle from '../../../components/ui/Toggle';
import type { DomainAlias } from '../types';

interface AliasFormModalProps {
  isOpen: boolean;
  aliasForm: DomainAlias;
  aliasErrors: Partial<Record<keyof DomainAlias, string>>;
  editingAlias: DomainAlias | null;
  inputClasses: (hasError?: boolean) => string;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  onInputChange: (field: keyof DomainAlias, value: DomainAlias[keyof DomainAlias]) => void;
}

export function AliasFormModal({
  isOpen,
  aliasForm,
  aliasErrors,
  editingAlias,
  inputClasses,
  onClose,
  onSubmit,
  onInputChange
}: AliasFormModalProps) {
  const { t } = useI18n();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {editingAlias ? t('telephonyDomains.aliases.editTitle') : t('telephonyDomains.aliases.addTitle')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-muted-foreground transition-colors hover:text-foreground"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('telephonyDomains.aliases.fields.alias')} *
            </label>
            <input
              type="text"
              value={aliasForm.alias ?? ''}
              onChange={(e) => onInputChange('alias', e.target.value)}
              placeholder={t('telephonyDomains.aliases.placeholders.alias')}
              className={inputClasses(!!aliasErrors.alias)}
            />
            {aliasErrors.alias && (
              <p className="mt-1 text-xs text-red-600">{aliasErrors.alias}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('telephonyDomains.aliases.fields.description')}
            </label>
            <input
              type="text"
              value={aliasForm.description ?? ''}
              onChange={(e) => onInputChange('description', e.target.value)}
              placeholder={t('telephonyDomains.aliases.placeholders.description')}
              className={inputClasses()}
            />
          </div>

          <div className="flex items-center gap-2">
            <Toggle
              value={aliasForm.enabled}
              onChange={(value) => onInputChange('enabled', value)}
            />
            <label className="text-sm font-medium text-foreground">
              {t('telephonyDomains.enabled')}
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="rounded-md bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


